import { type ReactNode, useId } from "react";
import styles from "./Field.module.css";

type FieldShellProps = {
  label: string;
  hint?: string;
  errors?: string[];
  children: (props: {
    id: string;
    describedBy: string | undefined;
    isInvalid: boolean;
  }) => ReactNode;
};

// One shell for every control, so the label, hint and error markup cannot
// drift between field types.
const FieldShell = ({ label, hint, errors, children }: FieldShellProps) => {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const isInvalid = errors !== undefined && errors.length > 0;
  const describedBy =
    [hint === undefined ? null : hintId, isInvalid ? errorId : null]
      .filter((value) => value !== null)
      .join(" ") || undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy, isInvalid })}
      {hint === undefined ? null : (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {isInvalid ? (
        <p id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </p>
      ) : null}
    </div>
  );
};

type TextFieldProps = {
  name: string;
  label: string;
  hint?: string;
  errors?: string[];
  defaultValue?: string;
  placeholder?: string;
  type?: "text" | "number" | "datetime-local";
  isDisabled?: boolean;
  // Controlled only where one field derives another: the code follows the
  // title, so a single owner has to hold both values. Passing `value` makes
  // this field controlled and `defaultValue` is then ignored.
  value?: string;
  onValueChange?: (next: string) => void;
};

export const TextField = ({
  name,
  label,
  hint,
  errors,
  defaultValue,
  placeholder,
  type = "text",
  isDisabled,
  value,
  onValueChange,
}: TextFieldProps) => (
  <FieldShell label={label} hint={hint} errors={errors}>
    {({ id, describedBy, isInvalid }) => (
      <input
        id={id}
        name={name}
        type={type}
        className={styles.input}
        placeholder={placeholder}
        disabled={isDisabled}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        {...(value === undefined
          ? { defaultValue }
          : {
              value,
              onChange: (event) => onValueChange?.(event.target.value),
            })}
      />
    )}
  </FieldShell>
);

type CheckboxFieldProps = {
  name: string;
  label: string;
  hint?: string;
  errors?: string[];
  isChecked: boolean;
};

// One box, one fact. Not a CheckboxGroup of length one: an unchecked box posts
// nothing, so the field is read with `get` rather than `getAll` and the value
// is its presence.
//
// Keyed on the intended state for the same reason the select is: React never
// moves an uncontrolled input once mounted, so a defaultChecked that changes
// after a save would leave the old state showing.
export const CheckboxField = ({
  name,
  label,
  hint,
  errors,
  isChecked,
}: CheckboxFieldProps) => {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const isInvalid = errors !== undefined && errors.length > 0;

  return (
    <div className={styles.field}>
      <label className={styles.checkbox} htmlFor={id}>
        <input
          key={String(isChecked)}
          id={id}
          name={name}
          type="checkbox"
          defaultChecked={isChecked}
          aria-invalid={isInvalid || undefined}
          aria-describedby={hint === undefined ? undefined : hintId}
        />
        {label}
      </label>
      {hint === undefined ? null : (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {isInvalid ? (
        <p id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </p>
      ) : null}
    </div>
  );
};

export type SelectOption = { value: string; label: string };

type SelectFieldProps = {
  name: string;
  label: string;
  hint?: string;
  errors?: string[];
  options: readonly SelectOption[];
  defaultValue?: string;
  isDisabled?: boolean;
};

// A closed set of values is a select, never a text box: the DB CHECK behind it
// is then unbreakable from the UI rather than merely validated.
//
// The key is load-bearing. React never moves an uncontrolled select once it is
// mounted, so a defaultValue that changes after a save leaves the old option
// showing; making it controlled fails differently, because React resets the
// form after a Server Action and that reset rewrites the DOM without telling
// React. Keying on the intended value mounts a fresh select whenever the value
// the server reports actually changes, and leaves the user's own in-progress
// choice alone — their picking an option does not change defaultValue.
export const SelectField = ({
  name,
  label,
  hint,
  errors,
  options,
  defaultValue,
  isDisabled,
}: SelectFieldProps) => (
  <FieldShell label={label} hint={hint} errors={errors}>
    {({ id, describedBy, isInvalid }) => (
      <>
        <select
          key={defaultValue}
          id={id}
          // A disabled control posts nothing, so a locked field would reach the
          // action as an absent value and fail its own validation. The name
          // moves to a hidden input carrying the value that cannot change.
          name={isDisabled ? undefined : name}
          className={styles.input}
          defaultValue={defaultValue}
          disabled={isDisabled}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {isDisabled ? (
          <input type="hidden" name={name} value={defaultValue ?? ""} />
        ) : null}
      </>
    )}
  </FieldShell>
);

type CheckboxGroupProps = {
  name: string;
  label: string;
  hint?: string;
  errors?: string[];
  options: readonly SelectOption[];
  defaultValues: readonly string[];
};

// A set, not a value: participation is 1-4 of a closed four, so it posts one
// entry per checked box and the action reads it with getAll.
export const CheckboxGroup = ({
  name,
  label,
  hint,
  errors,
  options,
  defaultValues,
}: CheckboxGroupProps) => {
  const groupId = useId();
  const errorId = `${groupId}-error`;
  const isInvalid = errors !== undefined && errors.length > 0;

  return (
    <fieldset className={styles.field}>
      <legend className={styles.label}>{label}</legend>
      <div
        className={styles.checkboxes}
        aria-describedby={isInvalid ? errorId : undefined}
      >
        {options.map((option) => (
          <label key={option.value} className={styles.checkbox}>
            <input
              type="checkbox"
              name={name}
              value={option.value}
              defaultChecked={defaultValues.includes(option.value)}
            />
            {option.label}
          </label>
        ))}
      </div>
      {hint === undefined ? null : <p className={styles.hint}>{hint}</p>}
      {isInvalid ? (
        <p id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </p>
      ) : null}
    </fieldset>
  );
};
